import 'react-circular-progressbar/dist/styles.css';
import { classnames, getQueryParam } from 'helpers/utils';
import { useContext, useEffect, useState } from 'react';
import { Checkbox } from 'common/checkbox';
import { CheckDashIcon, PlusIcon } from 'assets/icons';
import { VaultsController } from 'networking/controllers/vaults-controller';
import { Notification } from 'common/notification';
import { NotificationType } from 'common/enums';
import { appActions, AppContext } from 'context';
import { NotificationObject } from 'models/notificationObject';
import { goToPage, RouteName } from 'routes';
import styles from './onboarding-prompt-categories.module.scss';
import { PrompCategoriesLeftSection } from './components/left-section/prompt-categories-left-section';
import { PrompCategorieRightSection } from './components/right-section/prompt-categories-right-section';
import { AvatarStack } from './components/avatar-stack/avatar-stack';

const OnboardingPromptCategories: React.FC = () => {
  const { dispatch, state } = useContext(AppContext);
  const questionsList = getQueryParam('selected') ?? '';
  const vaultId = getQueryParam('vaultId') ?? '';
  const [selectedList, setSelectedList] = useState<PreBuiltQuestionsType[]>([]);
  const [selectedQuestion, setSelectedQuestions] = useState<QuestionType[]>([]);
  const [selectedUnansweredQuestions, setSelectedUnansweredQuestions] =
    useState<QuestionType[]>([]);

  const [unansweredQuestionsList, setUnansweredQuestionsList] = useState<
    ClipType[]
  >(state.currentVault?.unansweredQuestions ?? []);
  const [contributorsList, setContributorsList] = useState<ContributorType[]>(
    state.currentVault?.contributors ?? [],
  );
  const [vaultOwner, setVaultOwner] = useState<OwnerInfoType>();

  const [visibleCount, setVisibleCount] = useState(3);

  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (state.currentVault) {
      setVaultOwner(state.currentVault.owner);
      setContributorsList(state.currentVault.contributors);
      setUnansweredQuestionsList(state.currentVault.unansweredQuestions ?? []);
    }

    if (unansweredQuestionsList !== undefined) {
      setSelectedUnansweredQuestions((prev) => {
        let updated = [...prev];

        unansweredQuestionsList.forEach((question) => {
          const isSelected = updated.some((q) => q.id === question.id);
          if (isSelected) {
            updated = updated.filter((q) => q.id !== question.id);
          } else {
            updated.push(question);
          }
        });

        return updated;
      });
    }
  }, []);

  const showErrorNotification = () => (
    <Notification
      message="There was an error. Please try again later"
      handleClose={() => setShowError(false)}
    />
  );

  const toggleSelection = (
    question: QuestionType,
    setSelected: React.Dispatch<React.SetStateAction<QuestionType[]>>,
  ) => {
    setSelected((prev) => {
      const isSelected = prev.some((q) => q.id === question.id);
      return isSelected
        ? prev.filter((q) => q.id !== question.id)
        : [...prev, question];
    });
  };

  useEffect(() => {
    const parseList = JSON.parse(questionsList) as PreBuiltQuestionsType[];
    setSelectedList(parseList);
  }, [questionsList]);

  const handleShowMoreQuestions = () => {
    setVisibleCount((prev) =>
      Math.min(prev + 3, unansweredQuestionsList.length),
    );
  };

  const handleLess = () => {
    setVisibleCount(3);
  };

  const handleCreateAlbums = async () => {
    const creationPromises = selectedQuestion.map(async (question) => {
      try {
        const response = await VaultsController.createAlbum(
          Number(vaultId),
          question.name,
        );
        return { status: 'fulfilled', value: response };
      } catch (error) {
        return { status: 'rejected', reason: error };
      }
    });
    const results = await Promise.allSettled(creationPromises);
    const failedCount = results.filter(
      (result) => result.status === 'rejected',
    ).length;
    const totalCount = selectedQuestion.length;
    const successCount = totalCount - failedCount;
    if (failedCount === 0) {
      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'All  topics added successfully.',
          type: NotificationType.Success,
        }),
      });
    } else {
      const message =
        successCount > 0
          ? `${successCount} albums created. ${failedCount} failed. Please try again.`
          : 'Failed to create albums. Please try again.';

      dispatch({
        type: appActions.NOTIFICATION,
        notification: new NotificationObject({
          show: true,
          title: 'Some topics were not added correctly',
          message,
          type: NotificationType.Error,
        }),
      });
    }
  };

  const handleContinue = () => {
    handleCreateAlbums()
      .then(() => {
        goToPage(RouteName.VaultDetail, { id: vaultId }, { showModal: true });
      })
      .catch(() => {
        dispatch({
          type: appActions.NOTIFICATION,
          notification: new NotificationObject({
            show: true,
            title: 'Something went wrong',
            message: 'Please try again.',
            type: NotificationType.Error,
          }),
        });
      });
  };

  const getCreatorNamesList = (): string[] => {
    const namesSet = new Set<string>();

    unansweredQuestionsList.forEach((clip) => {
      if (vaultOwner && clip.createdBy === vaultOwner.id) {
        namesSet.add(vaultOwner.name);
      } else {
        const contributor = contributorsList.find(
          (c) =>
            c.id === clip.createdBy || c.idContributorAux === clip.createdBy,
        );
        if (contributor) {
          namesSet.add(contributor.name);
        }
      }
    });

    return Array.from(namesSet);
  };

  const renderUnansweredCard = () => {
    if (!unansweredQuestionsList.length) return null;

    const visibleUnanswered = unansweredQuestionsList.slice(0, visibleCount);
    const hasMoreUnanswered = unansweredQuestionsList.length > visibleCount;

    return (
      <div className={styles.card}>
        <div className={styles.titleContent}>
          <span className="text__body__semi__bold__large__gray900">
            Added by
          </span>
          <AvatarStack names={getCreatorNamesList()} />
        </div>
        <div className={styles.content}>
          {visibleUnanswered.map((question) => (
            <div key={question.id} className={styles.questions}>
              <div className={styles.checkbox}>
                <Checkbox
                  itemKey={String(question.id ?? '')}
                  isChecked={selectedUnansweredQuestions.includes(question)}
                  onChange={() => {}}
                  disabled
                  disabledColor="#C8C4BE"
                />
              </div>
              <label
                htmlFor={String(question.id ?? '')}
                className="text__body__regular__medium__gray800"
              >
                {question.name}
              </label>
            </div>
          ))}
          {unansweredQuestionsList.length > 3 && (
            <button
              type="button"
              className={classnames(
                styles.moreButton,
                'text__body__regular__medium__gray500',
              )}
              onClick={hasMoreUnanswered ? handleShowMoreQuestions : handleLess}
            >
              {hasMoreUnanswered ? <PlusIcon /> : <CheckDashIcon />}{' '}
              {hasMoreUnanswered ? 'More' : 'Less'}
            </button>
          )}
        </div>
      </div>
    );
  };

  const showLeftSection = () => (
    <PrompCategoriesLeftSection
      questionSelected={selectedQuestion.length}
      unansweredQuestions={unansweredQuestionsList}
      contributors={contributorsList}
      owner={vaultOwner}
      handleContinue={handleContinue}
    />
  );

  const showRightSection = () => (
    <>
      {unansweredQuestionsList.length !== 0 && renderUnansweredCard()}
      {selectedList.map((data) => (
        <PrompCategorieRightSection
          key={data.id}
          selected={selectedQuestion}
          toggleSelection={(question) =>
            toggleSelection(question, setSelectedQuestions)
          }
          questions={data.questions}
          title={data.name}
          imageSrc={data.imageUrl}
        />
      ))}
    </>
  );

  return (
    <>
      {showError && showErrorNotification()}
      <div className={styles.container}>
        <div className={styles.leftSection}>{showLeftSection()}</div>
        <div className={styles.contentCards}>{showRightSection()}</div>
      </div>
    </>
  );
};

export { OnboardingPromptCategories };
